import type { ProtocolRecord } from "Order/Modules/MorningCheck/Api/Query/useOrderProtocolsQuery";
import type { PartnershipRecord } from "Order/Modules/MorningCheck/Api/Query/usePartnershipRecordsQuery";
import type { ActivePartnerInfo } from "Order/Utilities/deriveActivePartner";
import ArrowForward from "@mui/icons-material/ArrowForward";
import AttachFile from "@mui/icons-material/AttachFile";
import Cancel from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import HourglassEmpty from "@mui/icons-material/HourglassEmpty";
import Link from "@mui/icons-material/Link";
import Person from "@mui/icons-material/Person";
import PersonOutline from "@mui/icons-material/PersonOutline";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Search from "@mui/icons-material/Search";
import TaskAlt from "@mui/icons-material/TaskAlt";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Paper,
	Stack,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { FC } from "react";

dayjs.extend(relativeTime);

const ACTION_TYPE_CONFIG = {
	connection: {
		label: "Connection",
		icon: <Link fontSize="small" />,
		positiveAction: "connected",
		negativeAction: "not_connected",
		positiveLabel: "Connected",
		negativeLabel: "Not Connected",
	},
	job_start: {
		label: "Job Start",
		icon: <PlayArrow fontSize="small" />,
		positiveAction: "started",
		negativeAction: "not_started",
		positiveLabel: "Started",
		negativeLabel: "Not Started",
		protocolType: "start" as const,
	},
	completion: {
		label: "Completion",
		icon: <TaskAlt fontSize="small" />,
		positiveAction: "finished",
		negativeAction: "not_finished",
		positiveLabel: "Finished",
		negativeLabel: "Not Finished",
		protocolType: "handover" as const,
	},
	examination: {
		label: "Examination",
		icon: <Search fontSize="small" />,
		positiveAction: "examined",
		negativeAction: "not_examined",
		positiveLabel: "Examined",
		negativeLabel: "Not Examined",
	},
} as const;

const STEP_ORDER: Array<keyof typeof ACTION_TYPE_CONFIG> = [
	"connection",
	"job_start",
	"completion",
	"examination",
];

/** Steps that have migrated to the digital protocol */
const PROTOCOL_STEPS = new Set<string>(["job_start", "completion"]);

function getFilePublicUrl(filePath: string): string {
	return `https://sovmasdqgraztfwatuwb.supabase.co/storage/v1/object/public/partnership-files/${filePath}`;
}

function parseExaminationReason(reason: string | null): {
	description?: string;
	realizationDate?: string;
	priceAdjusted?: boolean;
	newPrice?: string | null;
	files?: string[];
} | null {
	if (!reason) return null;
	try {
		return JSON.parse(reason);
	} catch {
		return null;
	}
}

type MorningCheckTimelineProps = {
	records: PartnershipRecord[];
	protocols?: ProtocolRecord[];
	getPartnerAtTime?: (time: string) => ActivePartnerInfo | null;
	resolvePartnerName?: (partnerId: string) => string;
};

export const MorningCheckTimeline: FC<MorningCheckTimelineProps> = ({
	records,
	protocols = [],
	getPartnerAtTime,
	resolvePartnerName,
}) => {
	const recordsByType = new Map<string, PartnershipRecord[]>();
	for (const record of records) {
		const existing = recordsByType.get(record.action_type) ?? [];
		existing.push(record);
		recordsByType.set(record.action_type, existing);
	}

	// Build protocol lookup: type -> ProtocolRecord (start -> job_start, handover -> completion)
	const protocolByStep = new Map<string, ProtocolRecord>();
	for (const p of protocols) {
		const stepKey = p.type === "start" ? "job_start" : "completion";
		const existing = protocolByStep.get(stepKey);
		if (!existing || new Date(p.dateCreated) > new Date(existing.dateCreated)) {
			protocolByStep.set(stepKey, p);
		}
	}

	const isStepComplete = (type: keyof typeof ACTION_TYPE_CONFIG): boolean => {
		if (PROTOCOL_STEPS.has(type)) {
			const proto = protocolByStep.get(type);
			if (proto) return proto.status === "accepted";
		}
		const typeRecords = recordsByType.get(type);
		if (!typeRecords || typeRecords.length === 0) return false;
		return typeRecords.some(
			(r) => r.action === ACTION_TYPE_CONFIG[type].positiveAction,
		);
	};

	const activeStep = STEP_ORDER.findIndex((type) => !isStepComplete(type));

	return (
		<Stepper
			orientation="vertical"
			activeStep={activeStep === -1 ? STEP_ORDER.length : activeStep}
			sx={{ width: "100%" }}
		>
			{STEP_ORDER.map((type) => {
				const config = ACTION_TYPE_CONFIG[type];
				const typeRecords = recordsByType.get(type) ?? [];
				const protocol = protocolByStep.get(type);
				const hasProtocol = !!protocol;
				const completed = isStepComplete(type);
				const hasRecords = typeRecords.length > 0 || hasProtocol;

				const latestRecord =
					typeRecords.length > 0 ? typeRecords[typeRecords.length - 1] : null;
				const latestPartnerInfo =
					latestRecord && getPartnerAtTime
						? getPartnerAtTime(latestRecord.created_at)
						: null;
				const latestPartnerName =
					latestPartnerInfo && resolvePartnerName
						? resolvePartnerName(latestPartnerInfo.partnerId)
						: null;

				return (
					<Step key={type} completed={completed}>
						<StepLabel
							icon={
								completed ? (
									<CheckCircle color="success" />
								) : hasRecords ? (
									hasProtocol && protocol.status === "sent" ? (
										<HourglassEmpty color="warning" />
									) : (
										<Cancel color="error" />
									)
								) : (
									<HourglassEmpty color="disabled" />
								)
							}
						>
							<Stack
								direction="row"
								alignItems="center"
								gap={1}
								flexWrap="wrap"
							>
								{config.icon}
								<Typography fontWeight={600}>{config.label}</Typography>
								{completed && (
									<Chip
										label={config.positiveLabel}
										color="success"
										size="small"
										variant="outlined"
									/>
								)}
								{hasRecords && !completed && !hasProtocol && (
									<Chip
										label={config.negativeLabel}
										color="error"
										size="small"
										variant="outlined"
									/>
								)}
								{!hasRecords && (
									<Chip
										label="Pending"
										size="small"
										variant="outlined"
										color="default"
									/>
								)}
								{hasProtocol && !completed && (
									<Chip
										label={
											protocol.status === "sent"
												? "Awaiting Customer"
												: protocol.status === "declined"
													? "Customer Declined"
													: "Draft"
										}
										color={
											protocol.status === "sent"
												? "warning"
												: protocol.status === "declined"
													? "error"
													: "default"
										}
										size="small"
										variant="outlined"
									/>
								)}
								{latestRecord && (latestPartnerName || hasRecords) && (
									<Typography variant="caption" color="text.secondary">
										{latestPartnerName ? (
											<>
												— <strong>{latestPartnerName}</strong> —{" "}
												{dayjs(latestRecord.created_at).format("DD/MM HH:mm")}
											</>
										) : (
											<>
												— {dayjs(latestRecord.created_at).format("DD/MM HH:mm")}
											</>
										)}
									</Typography>
								)}
							</Stack>
						</StepLabel>
						<StepContent>
							<Stack gap={1}>
								{hasProtocol && (
									<ProtocolCard
										protocol={protocol}
										resolvePartnerName={resolvePartnerName}
									/>
								)}
								{typeRecords.map((record) => {
									const partnerInfo =
										getPartnerAtTime?.(record.created_at) ?? null;
									const partnerName =
										partnerInfo && resolvePartnerName
											? resolvePartnerName(partnerInfo.partnerId)
											: null;
									return (
										<RecordCard
											key={record.id}
											record={record}
											config={config}
											partnerName={partnerName}
										/>
									);
								})}
								{!hasRecords && (
									<Typography variant="body2" color="text.secondary">
										No update from the partner yet.
									</Typography>
								)}
							</Stack>
						</StepContent>
					</Step>
				);
			})}
		</Stepper>
	);
};

/* --- Protocol card (digital handover protocol) --- */

type ProtocolCardProps = {
	protocol: ProtocolRecord;
	resolvePartnerName?: (partnerId: string) => string;
};

const PROTOCOL_STATUS_COLOR: Record<
	string,
	"success" | "warning" | "error" | "default"
> = {
	accepted: "success",
	sent: "warning",
	declined: "error",
	draft: "default",
};

const ProtocolCard: FC<ProtocolCardProps> = ({
	protocol,
	resolvePartnerName,
}) => {
	const partnerName = resolvePartnerName?.(String(protocol.partnerId));
	const statusColor = PROTOCOL_STATUS_COLOR[protocol.status] ?? "default";
	const isAutoApproved = protocol.customerDecision?.autoApproved === true;

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 1.5,
				borderColor: `${statusColor}.light`,
				borderLeftWidth: 3,
				bgcolor: "action.hover",
			}}
		>
			<Stack gap={1}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Chip
						label={`Protocol: ${protocol.type === "start" ? "Start" : "Handover"}`}
						size="small"
						color={statusColor}
						sx={{ textTransform: "capitalize" }}
					/>
					<Typography variant="caption" color="text.secondary">
						{dayjs(protocol.dateCreated).format("DD/MM HH:mm")} (
						{dayjs(protocol.dateCreated).fromNow()})
					</Typography>
				</Stack>

				{/* Two-part progress: Partner -> Customer */}
				<Stack gap={0.5}>
					<Stack direction="row" alignItems="center" gap={0.75}>
						<Person
							fontSize="small"
							color={protocol.sentAt ? "success" : "disabled"}
						/>
						<Typography variant="body2" fontWeight={500}>
							Partner{" "}
							{partnerName ? `(${partnerName})` : `#${protocol.partnerId}`}
						</Typography>
						{protocol.sentAt ? (
							<Chip
								label={`Sent ${dayjs(protocol.sentAt).format("DD/MM HH:mm")}`}
								size="small"
								color="success"
								variant="outlined"
							/>
						) : (
							<Chip
								label="Draft"
								size="small"
								variant="outlined"
								color="default"
							/>
						)}
					</Stack>

					{protocol.sentAt && (
						<Stack
							direction="row"
							alignItems="center"
							gap={0.75}
							sx={{ pl: 0.5 }}
						>
							<ArrowForward
								fontSize="small"
								color={protocol.customerAcceptedAt ? "success" : "disabled"}
							/>
						</Stack>
					)}

					{protocol.sentAt && (
						<Stack direction="row" alignItems="center" gap={0.75}>
							<PersonOutline
								fontSize="small"
								color={protocol.customerAcceptedAt ? "success" : "disabled"}
							/>
							<Typography variant="body2" fontWeight={500}>
								Customer
								{protocol.customerName ? ` (${protocol.customerName})` : ""}
							</Typography>
							{protocol.status === "accepted" && protocol.customerAcceptedAt ? (
								isAutoApproved ? (
									<Chip
										label={`Auto-confirmed after 12h · ${dayjs(protocol.customerAcceptedAt).format("DD/MM HH:mm")}`}
										size="small"
										color="warning"
										variant="outlined"
									/>
								) : (
									<Chip
										label={`Accepted ${dayjs(protocol.customerAcceptedAt).format("DD/MM HH:mm")}`}
										size="small"
										color="success"
										variant="outlined"
									/>
								)
							) : protocol.status === "declined" ? (
								<Chip
									label="Declined"
									size="small"
									color="error"
									variant="outlined"
								/>
							) : (
								<Chip
									label="Awaiting response"
									size="small"
									color="warning"
									variant="outlined"
								/>
							)}
						</Stack>
					)}
				</Stack>

				{protocol.finalPrice != null && (
					<Typography variant="body2" color="text.secondary">
						<strong>Agreed price:</strong> {protocol.finalPrice}
					</Typography>
				)}

				{protocol.priceAdjustmentNotes && (
					<Typography variant="body2" color="text.secondary">
						<strong>Partner notes:</strong> {protocol.priceAdjustmentNotes}
					</Typography>
				)}

				{protocol.customerComment && (
					<Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
						<CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
							<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
								<strong>Customer comment:</strong> {protocol.customerComment}
							</Typography>
						</CardContent>
					</Card>
				)}

				{isAutoApproved && protocol.status === "accepted" && (
					<Typography variant="caption" color="text.secondary">
						Confirmed automatically after 12 hours without customer response —
						no action from the customer was required.
					</Typography>
				)}
			</Stack>
		</Paper>
	);
};

/* --- Legacy Supabase record card --- */

type RecordCardProps = {
	record: PartnershipRecord;
	config: (typeof ACTION_TYPE_CONFIG)[keyof typeof ACTION_TYPE_CONFIG];
	partnerName?: string | null;
};

const RecordCard: FC<RecordCardProps> = ({ record, config, partnerName }) => {
	const isPositive = record.action === config.positiveAction;
	const examinationData =
		record.action_type === "examination"
			? parseExaminationReason(record.reason)
			: null;

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 1.5,
				borderColor: isPositive ? "success.light" : "error.light",
				borderLeftWidth: 3,
			}}
		>
			<Stack gap={0.5}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Chip
						label={record.action.replace(/_/g, " ")}
						size="small"
						color={isPositive ? "success" : "error"}
						sx={{ textTransform: "capitalize" }}
					/>
					<Stack direction="row" alignItems="center" gap={0.5}>
						{partnerName && (
							<Typography
								variant="caption"
								fontWeight={600}
								color="text.secondary"
							>
								{partnerName} —
							</Typography>
						)}
						<Typography variant="caption" color="text.secondary">
							{dayjs(record.created_at).format("DD/MM HH:mm")} (
							{dayjs(record.created_at).fromNow()})
						</Typography>
					</Stack>
				</Stack>

				{!isPositive && record.reason && !examinationData && (
					<Box mt={0.5}>
						<Typography variant="body2" color="text.secondary">
							<strong>Reason:</strong> {record.reason}
						</Typography>
					</Box>
				)}

				{record.note_for_adam && (
					<Card variant="outlined" sx={{ mt: 0.5, bgcolor: "action.hover" }}>
						<CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
							<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
								<strong>Note for Adam:</strong> {record.note_for_adam}
							</Typography>
						</CardContent>
					</Card>
				)}

				{examinationData && (
					<Box mt={0.5}>
						{examinationData.description && (
							<Typography variant="body2" color="text.secondary">
								<strong>Description:</strong> {examinationData.description}
							</Typography>
						)}
						{examinationData.realizationDate && (
							<Typography variant="body2" color="text.secondary">
								<strong>Realization date:</strong>{" "}
								{dayjs(examinationData.realizationDate).format("DD MMM YYYY")}
							</Typography>
						)}
						{examinationData.priceAdjusted && examinationData.newPrice && (
							<Typography variant="body2" color="warning.main">
								<strong>Price adjusted:</strong> {examinationData.newPrice}
							</Typography>
						)}
						{examinationData.files && examinationData.files.length > 0 && (
							<Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap">
								{examinationData.files.map((filePath) => (
									<Chip
										key={filePath}
										icon={<AttachFile fontSize="small" />}
										label="View file"
										size="small"
										component="a"
										href={getFilePublicUrl(filePath)}
										target="_blank"
										rel="noopener noreferrer"
										clickable
										variant="outlined"
									/>
								))}
							</Stack>
						)}
					</Box>
				)}

				{record.action_type === "completion" && record.file_path && (
					<Stack direction="row" gap={0.5} mt={0.5}>
						<Chip
							icon={<AttachFile fontSize="small" />}
							label="View completion file"
							size="small"
							component="a"
							href={getFilePublicUrl(record.file_path)}
							target="_blank"
							rel="noopener noreferrer"
							clickable
							variant="outlined"
							color="primary"
						/>
					</Stack>
				)}
			</Stack>
		</Paper>
	);
};
